import { Component, DestroyRef, OnInit, computed, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { KENDO_DATEINPUTS } from "@progress/kendo-angular-dateinputs";
import { KENDO_SVGICON } from "@progress/kendo-angular-icons";
import { KENDO_TEXTBOX } from "@progress/kendo-angular-inputs";
import { warningTriangleIcon } from "@progress/kendo-svg-icons";
import { BootstrapService } from "../../../../../shared/bootstrap/bootstrap.service";
import { offsetFromUtc, offsetText } from "../../../../../shared/time-zone-offset";
import { FlightGroup } from "../../../form/flight-form-group";
import { durationsOf } from "./times-group";
import { ChangedFieldDirective } from "../../../changed-field.directive";

@Component({
    selector: "app-times-group",
    imports: [ChangedFieldDirective, KENDO_SVGICON, ReactiveFormsModule, TranslatePipe, KENDO_DATEINPUTS, KENDO_TEXTBOX],
    templateUrl: "./times-group.component.html",
    styleUrl: "../../../edit-fields.scss",
})
export class TimesGroupComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly bootstrap = inject(BootstrapService);

    readonly group = input.required<FlightGroup>();

    /** The flight's days and times are kept, shown and edited in the organization's time zone, whatever the grid shows. */
    readonly organizationZone = this.bootstrap.organizationTimeZone;
    readonly organizationOffset = computed(() => {
        const minutes = offsetFromUtc(this.organizationZone(), new Date());
        return minutes === null ? "" : offsetText(minutes);
    });

    readonly icons = { warning: warningTriangleIcon };

    /** Each moment of the flight as the logbook keeps it: a day and a time of day. */
    readonly moments = [
        { control: "startTime", date: "startDate", title: "FLIGHT_LIST.edit.times.start", dateTitle: "FLIGHT_LIST.startDate" },
        { control: "takeOffTime", date: "flightDate", title: "FLIGHT_LIST.edit.times.takeOff", dateTitle: "FLIGHT_LIST.flightDate" },
        { control: "landingTime", date: "arrivalDate", title: "FLIGHT_LIST.edit.times.landing", dateTitle: "FLIGHT_LIST.arrivalDate" },
        { control: "stopTime", date: "stopDate", title: "FLIGHT_LIST.edit.times.stop", dateTitle: "FLIGHT_LIST.stopDate" },
    ] as const;

    /** Worked out from the days and times the form holds, shown read only. */
    readonly durations = signal<{ flightTime: string | null; startStopTime: string | null }>({ flightTime: null, startStopTime: null });

    /** What the durations were when the dialog opened, to show the ones that now differ. */
    private opened = { flightTime: null as string | null, startStopTime: null as string | null };

    readonly flightTimeChanged = computed(() => this.durations().flightTime !== this.opened.flightTime);
    readonly startStopTimeChanged = computed(() => this.durations().startStopTime !== this.opened.startStopTime);

    ngOnInit(): void {
        this.opened = durationsOf(this.group().getRawValue());
        this.durations.set(this.opened);
        this.group()
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.durations.set(durationsOf(this.group().getRawValue())));
    }
}
